import React, { useRef, MouseEvent } from 'react'
import styled, { css } from 'styled-components'
import _ from 'lodash'

import { BriqTheme, ThemeWrapper, themeColor } from '../theme'
import { backgroundTransition } from '../Animations'
import { breakpoint } from '../theme'
import { useClickOutside } from '../../hooks/useClickOutside'
import { Dropdown } from '../Dropdown'

export type Link = {
  url: string
  text: string
  target?: string
}

export type DropDownLink = {
  header: string
  links: Link[]
}

export type NavigationLink = {
  linkModel?: Link
  dropdownLinksMenuModel?: DropDownLink
}

type NavMenuVariant = 'primary' | 'primaryDark'

function getHoverColorByVariant(variant: NavMenuVariant) {
  switch (variant) {
    case 'primary':
      return 'primaryLight'
    case 'primaryDark':
      return 'primary'
  }
}

const StyledNavMenu = styled.nav`
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  flex-grow: 1;
  ${breakpoint.tablet(css`
    flex-direction: row;
  `)}

  a {
    display: flex;
    flex: 1 1 auto;
  }
`

const StyledNavMenuItem = styled.div<{ variant: NavMenuVariant; active?: boolean; theme?: BriqTheme }>`
  flex: 1 1 auto;
  position: relative;
  text-align: center;
  cursor: pointer;
  font-weight: bold;
  color: ${({ active }) => themeColor(active ? 'secondary' : 'white')};
  padding: 14px;
  background: ${({ variant }) => themeColor(variant)};

  &:hover {
    background: ${({ variant }) => themeColor(getHoverColorByVariant(variant))};
  }

  ${backgroundTransition};

  &::before {
    content: ' ';
    position: absolute;
    top: 20%;
    left: calc(50% - 3px);
    display: ${({ active }) => (active ? 'block' : 'none')};
    background: ${themeColor('secondary')};
    width: 6px;
    height: 6px;
    transform: rotate(45deg);
    border-radius: 1px;
  }
`
const StyledNavMenuOverlay = styled.div`
  margin-top: -7px;
`

const StyledDropDownNavMenuItem = ({
  link,
  variant,
  parentRef,
  isVisible,
  toggle,
  close
}: {
  link: DropDownLink
  variant: NavMenuVariant
  parentRef: React.RefObject<HTMLDivElement>
  isVisible: boolean
  toggle: () => void
  close: () => void
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)

  const handleOnVisibleChange = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    toggle()
  }

  useClickOutside(overlayRef, () => {
    close()
  })

  return (
    <Dropdown
      trigger={['click']}
      getPopupContainer={() => parentRef.current || document.body}
      open={isVisible}
      dropdownRender={() => (
        <StyledNavMenuOverlay ref={overlayRef}>
          {_.map(link.links, (l, i) => (
            <a href={l.url} key={i} target={l.target}>
              <StyledNavMenuItem variant={variant}>{l.text}</StyledNavMenuItem>
            </a>
          ))}
        </StyledNavMenuOverlay>
      )}
    >
      <StyledNavMenuItem variant={variant} active={isVisible} onClick={handleOnVisibleChange}>
        {link.header}
      </StyledNavMenuItem>
    </Dropdown>
  )
}

export interface NavMenuProps {
  variant: NavMenuVariant
  items: NavigationLink[]
  isVisible: (name: string) => boolean
  toggle: (name: string) => void
  close: (name: string) => void
}

export const NavMenu = ({ variant, items, isVisible, toggle, close }: NavMenuProps) => {
  const navMenuRef = useRef(null)
  return (
    <ThemeWrapper>
      <div ref={navMenuRef}>
        <StyledNavMenu>
          {_.map(items, (item, i) => (
            <React.Fragment key={i}>
              {item.linkModel && (
                <a href={item.linkModel.url} target={item.linkModel.target}>
                  <StyledNavMenuItem variant={variant}>{item.linkModel.text}</StyledNavMenuItem>
                </a>
              )}
              {item.dropdownLinksMenuModel && (
                <StyledDropDownNavMenuItem
                  link={item.dropdownLinksMenuModel}
                  variant={variant}
                  parentRef={navMenuRef}
                  isVisible={item.dropdownLinksMenuModel ? isVisible(item.dropdownLinksMenuModel.header) : false}
                  toggle={() => item.dropdownLinksMenuModel && toggle(item.dropdownLinksMenuModel.header)}
                  close={() => item.dropdownLinksMenuModel && close(item.dropdownLinksMenuModel.header)}
                />
              )}
            </React.Fragment>
          ))}
        </StyledNavMenu>
      </div>
    </ThemeWrapper>
  )
}
